import React, { type SVGProps } from 'react'

import { F0Box, F0Button, F0Icon, F0Text } from '@factorialco/f0-react'

import { FilterAccordion } from './FilterAccordion'

type IconComponent = React.ForwardRefExoticComponent<
  Omit<SVGProps<SVGSVGElement>, 'ref'> & React.RefAttributes<SVGSVGElement>
>

export type NavigationItem = {
  id: string
  label: string
  icon: IconComponent
  active?: boolean
  badge?: number
  onClick: (id: string) => void
}

export type CategoryItem = {
  id: string
  label: string
  icon: IconComponent
  active?: boolean
  onClick: (id: string) => void
}

export type FilterSection = {
  id: string
  label: string
  options: { value: string; label: string }[]
  selectedValues: string[]
  onChange: (values: string[]) => void
}

export type MarketplaceSidebarSection =
  | { type: 'navigation'; items: NavigationItem[] }
  | { type: 'categories'; title: string; items: CategoryItem[] }
  | { type: 'filters'; sections: FilterSection[]; onClearAll?: () => void }

type MarketplaceSidebarProps = {
  sections: MarketplaceSidebarSection[]
  activeItemId?: string
}

export const MarketplaceSidebar: React.FC<MarketplaceSidebarProps> = ({
  sections,
  activeItemId,
}) => {
  return (
    <F0Box display="flex" flexDirection="column" gap="md">
      {sections.map((section, idx) => {
        switch (section.type) {
          case 'navigation': {
            return (
              <F0Box key={`nav-${idx}`} display="flex" flexDirection="column" gap="xs">
                {section.items.map((item) => {
                  const isActive = activeItemId === item.id
                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={`flex gap-1.5 items-center py-2 px-3 rounded-sm cursor-pointer transition-colors ${
                        isActive ? 'bg-f1-background-secondary' : 'hover:bg-f1-background-secondary'
                      }`}
                      onClick={() => item.onClick(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') item.onClick(item.id)
                      }}
                    >
                      <F0Icon icon={item.icon} size="md" />
                      <F0Text variant="label" content={item.label} />
                      {item.badge !== undefined && item.badge > 0 && (
                        <F0Text variant="small" content={String(item.badge)} />
                      )}
                    </div>
                  )
                })}
              </F0Box>
            )
          }

          case 'categories': {
            return (
              <F0Box key={`cat-${idx}`} display="flex" flexDirection="column" gap="xs">
                <F0Box padding="sm">
                  <F0Text variant="description" content={section.title} />
                </F0Box>
                {section.items.map((item) => {
                  const isActive = activeItemId === item.id
                  return (
                    <div
                      key={item.id}
                      role="button"
                      tabIndex={0}
                      className={`flex gap-1.5 items-center py-2 px-3 rounded-sm cursor-pointer transition-colors ${
                        isActive ? 'bg-f1-background-secondary' : 'hover:bg-f1-background-secondary'
                      }`}
                      onClick={() => item.onClick(item.id)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') item.onClick(item.id)
                      }}
                    >
                      <F0Icon icon={item.icon} size="md" />
                      <F0Text variant="label" content={item.label} />
                    </div>
                  )
                })}
              </F0Box>
            )
          }

          case 'filters': {
            if (section.sections.length === 0) return null

            const hasActiveFilters = section.sections.some((s) => s.selectedValues.length > 0)

            return (
              <F0Box key={`filters-${idx}`} display="flex" flexDirection="column">
                {/* Separator line between categories and filters */}
                <div className="h-px bg-f1-border mx-3 my-1 mb-2" />
                {section.sections.map((filterSection, filterIdx) => (
                  <FilterAccordion
                    key={filterSection.id}
                    id={filterSection.id}
                    label={filterSection.label}
                    options={filterSection.options}
                    selectedValues={filterSection.selectedValues}
                    onChange={filterSection.onChange}
                    defaultExpanded={filterIdx === 0}
                  />
                ))}
                {hasActiveFilters && section.onClearAll && (
                  <F0Box padding="sm">
                    <F0Button
                      variant="outline"
                      size="sm"
                      label="Clear all filters"
                      onClick={section.onClearAll}
                    />
                  </F0Box>
                )}
              </F0Box>
            )
          }
        }
      })}
    </F0Box>
  )
}
